import { useId, useRef, useState } from "react";
import { Lock, LockOpen } from "lucide-react";
import { formatEUR } from "@/lib/format";
import { cn } from "@/lib/utils";
import { DemoFrame } from "./DemoFrame";
import {
  DEMO_CLIENTS,
  DEMO_SENDER,
  LATE_NOTICE_THRESHOLD,
  lateNoticeAvailable,
} from "./demoData";

const CLIENT = DEMO_CLIENTS[3]; // Transports Rhône-Est
const MAX_DAYS = 240;
/** Indemnité forfaitaire pour frais de recouvrement, par facture (D441-5). */
const INDEMNITY = 40;
const THRESHOLD_AT = (LATE_NOTICE_THRESHOLD / MAX_DAYS) * 100;

/**
 * Le seuil de la mise en demeure, comme dans l'app : fermée tant que la
 * créance la plus ancienne n'a pas dépassé le seuil (180 jours par défaut),
 * elle prépare ensuite un courrier à relire. Rien ne part tout seul.
 */
export function GateDemo() {
  const [days, setDays] = useState(150);
  const [prepared, setPrepared] = useState(false);
  const sliderId = useId();
  const hintId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = lateNoticeAvailable(days);

  const onPrepare = () => {
    if (open) {
      setPrepared(true);
      return;
    }
    // Refus poli : le bouton fait « non » de la tête (relancé à chaque clic).
    const el = buttonRef.current;
    if (!el) return;
    el.classList.remove("enc-nudge");
    void el.offsetWidth;
    el.classList.add("enc-nudge");
  };

  return (
    <DemoFrame
      title={
        <>
          {CLIENT.name} · {CLIENT.invoices} factures impayées ·{" "}
          <span className="whitespace-nowrap">{formatEUR(CLIENT.amount)}</span>
        </>
      }
      footnote="Données fictives · sous le seuil, le bouton reste verrouillé."
    >
      <label
        htmlFor={sliderId}
        className="flex items-baseline justify-between gap-3 text-ui font-semibold text-foreground"
      >
        Retard de la plus ancienne facture
        <span className="font-mono text-lg tabular-nums">{days} j</span>
      </label>

      <div className="relative mt-4 h-2">
        <input
          id={sliderId}
          type="range"
          min={0}
          max={MAX_DAYS}
          step={5}
          value={days}
          onChange={(e) => {
            setDays(Number(e.target.value));
            setPrepared(false);
          }}
          className="peer absolute inset-x-0 top-1/2 z-10 h-11 w-full -translate-y-1/2 cursor-pointer opacity-0"
        />
        <div className="absolute inset-0 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-colors duration-200",
              open ? "bg-age-90-bar" : "bg-age-61-90-bar",
            )}
            style={{ width: `${(days / MAX_DAYS) * 100}%` }}
          />
        </div>
        <span
          aria-hidden="true"
          className="absolute -top-1.5 h-5 w-0.5 -translate-x-1/2 rounded bg-primary"
          style={{ left: `${THRESHOLD_AT}%` }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-primary shadow-knob transition-[scale] duration-150 peer-hover:scale-110 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring peer-active:scale-95"
          style={{ left: `${(days / MAX_DAYS) * 100}%` }}
        />
      </div>
      <div className="relative mt-3 h-4 text-caption text-muted-foreground">
        <span
          className="absolute -translate-x-1/2 whitespace-nowrap"
          style={{ left: `${THRESHOLD_AT}%` }}
        >
          seuil {LATE_NOTICE_THRESHOLD} j, réglable
        </span>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          ref={buttonRef}
          type="button"
          onClick={onPrepare}
          onAnimationEnd={(e) => e.currentTarget.classList.remove("enc-nudge")}
          aria-disabled={open ? undefined : "true"}
          aria-describedby={hintId}
          className={cn(
            "inline-flex h-11 items-center gap-2 rounded-lg px-4 text-ui font-semibold transition-[background-color,color,translate,scale] duration-150",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
            open
              ? "cursor-pointer bg-action text-action-foreground shadow-raised hover:-translate-y-px hover:bg-action-strong active:scale-[0.97] active:duration-[70ms]"
              : "cursor-not-allowed border border-border bg-muted text-muted-foreground",
          )}
        >
          {/* Le cadenas s'ouvre en passant le seuil. */}
          <span key={open ? "open" : "locked"} className={cn("inline-flex", open && "enc-settle")}>
            {open ? (
              <LockOpen aria-hidden className="size-4" />
            ) : (
              <Lock aria-hidden className="size-4" />
            )}
          </span>
          Préparer la mise en demeure
        </button>
        <p id={hintId} aria-live="polite" className="text-caption text-muted-foreground">
          {open
            ? "Seuil dépassé\u00a0: le courrier peut être préparé."
            : `Disponible au-delà de ${LATE_NOTICE_THRESHOLD} jours de retard.`}
        </p>
      </div>

      {prepared && open && (
        <article
          aria-label="Aperçu du courrier"
          className="enc-paper-in relative mt-6 rounded-md border border-ink-rule bg-paper px-5 pt-6 pb-5 text-ink shadow-paper"
        >
          <span
            className="mb-3 inline-flex rounded-md border border-ink-rule px-2.5 py-1 text-caption font-semibold text-ink-muted"
          >
            Brouillon · à relire
          </span>
          <p className="text-caption text-ink-muted">
            {DEMO_SENDER.company} à {CLIENT.name}
          </p>
          <h4 className="mt-2 font-display text-xl font-semibold text-ink-strong">
            Mise en demeure de payer
          </h4>
          <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-ui">
            <dt>{CLIENT.invoices} factures restées impayées</dt>
            <dd className="text-right">{formatEUR(CLIENT.amount)}</dd>
            <dt>
              Indemnité forfaitaire pour frais de recouvrement ({CLIENT.invoices} × {formatEUR(INDEMNITY)})
            </dt>
            <dd className="text-right">
              {formatEUR(CLIENT.invoices * INDEMNITY)}
            </dd>
            <dt className="text-ink-muted">Pénalités de retard</dt>
            <dd className="text-right text-ink-muted">à la date du courrier</dd>
          </dl>
          <p className="mt-4 border-t border-ink-rule pt-3 text-caption text-ink-muted">
            Un aperçu à relire. La préparation est notée dans l’historique du
            client.
          </p>
        </article>
      )}
    </DemoFrame>
  );
}
